import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listSubmissions, reviewAppSubmission, type AppSubmission } from '@/lib/appValidation';

type Filter = 'all' | 'pending' | 'approved' | 'rejected';

export default function AppReviewTab() {
  const [submissions, setSubmissions] = useState<AppSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('pending');
  const [openId, setOpenId] = useState<number | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSubmissions(await listSubmissions());
    } catch {
      setError('Impossible de charger les soumissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (submission: AppSubmission, decision: 'approved' | 'rejected') => {
    setBusyId(submission.appId);
    setError(null);
    try {
      const updated = await reviewAppSubmission(submission.appId, decision, noteDrafts[submission.appId] || '');
      setSubmissions((prev) => prev.map((item) => (item.appId === submission.appId ? updated : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La décision n’a pas pu être enregistrée.');
    } finally {
      setBusyId(null);
    }
  };

  const copySql = async (submission: AppSubmission) => {
    try {
      await navigator.clipboard.writeText(submission.provisionSql);
      setCopiedId(submission.appId);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('La copie automatique est bloquée par le navigateur. Sélectionnez le script manuellement.');
    }
  };

  const filtered = submissions.filter((submission) => (filter === 'all' ? true : submission.status === filter));

  const statusMeta = (submission: AppSubmission) => {
    if (submission.status === 'approved') return { label: 'Approuvée', tone: 'bg-secondary-100 text-secondary-900' };
    if (submission.status === 'rejected') return { label: 'Refusée', tone: 'bg-red-50 text-red-700' };
    return { label: 'En attente', tone: 'bg-accent-100 text-accent-900' };
  };

  const verdictMeta = (submission: AppSubmission) => {
    if (submission.verdict === 'blocked') return { label: 'Risque élevé', tone: 'text-red-700' };
    if (submission.verdict === 'review') return { label: 'À vérifier', tone: 'text-accent-900' };
    return { label: 'Sûr', tone: 'text-secondary-900' };
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit">
          {[
            { key: 'pending', label: 'En attente' },
            { key: 'approved', label: 'Approuvées' },
            { key: 'rejected', label: 'Refusées' },
            { key: 'all', label: 'Toutes' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as Filter)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                filter === tab.key ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-background-200/70 rounded-lg">
          <i className="ri-shield-check-line text-3xl text-foreground-300 block mb-2"></i>
          <p className="text-sm text-foreground-500">Aucune application dans cette catégorie</p>
          <p className="text-xs text-foreground-400 mt-1">
            Les apps soumises depuis l'assistant IA du constructeur apparaissent ici.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((submission) => {
            const status = statusMeta(submission);
            const verdict = verdictMeta(submission);
            const expanded = openId === submission.appId;
            return (
              <article key={submission.appId} className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
                  <div className="w-11 h-11 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-apps-2-line text-foreground-500"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-foreground-900 truncate">{submission.appName}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${status.tone}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">
                      {submission.snapshot.pages.length} page(s) · {submission.snapshot.forms.length} formulaire(s) ·{' '}
                      {submission.snapshot.tables.length} table(s) · soumis le{' '}
                      {new Date(submission.submittedAt).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className={`text-base font-bold ${verdict.tone}`}>{submission.score}</p>
                      <p className="text-[9px] text-foreground-400">score</p>
                    </div>
                    <button
                      onClick={() => setOpenId(expanded ? null : submission.appId)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                      {expanded ? 'Réduire' : 'Examiner'}
                    </button>
                    <Link
                      to={`/superadmin/apps/${submission.appId}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap no-underline"
                    >
                      <i className="ri-tools-line"></i>
                      Ouvrir
                    </Link>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-background-200/50 p-4 space-y-3 bg-background-100/30">
                    <div className="flex flex-wrap items-center gap-3 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-background-50 border border-background-200/60 text-foreground-600">
                        Verdict : <span className={`font-semibold ${verdict.tone}`}>{verdict.label}</span>
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-background-50 border border-background-200/60 text-foreground-600">
                        {submission.findings.length} alerte(s)
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-background-50 border border-background-200/60 text-foreground-600">
                        Tables provisionnées : {submission.provisioned ? 'oui' : 'non'}
                      </span>
                    </div>

                    <p className="text-[11px] text-foreground-500 leading-relaxed">
                      <i className="ri-cloud-line mr-1"></i>
                      Après approbation, le contenu validé est publié et servi par l'API runtime
                      <code className="mx-1 px-1.5 py-0.5 rounded bg-background-50 border border-background-200/60 text-[10px]">
                        /functions/v1/zifek-app-runtime?app=&lt;slug&gt;
                      </code>
                      (lecture seule, applications publiées uniquement).
                    </p>

                    {submission.findings.length > 0 ? (                      <ul className="space-y-1.5">
                        {submission.findings.map((finding, index) => (
                          <li key={index} className="flex items-start gap-2 text-[11px] text-foreground-700">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase flex-shrink-0 ${
                                finding.severity === 'high'
                                  ? 'bg-red-100 text-red-700'
                                  : finding.severity === 'medium'
                                    ? 'bg-accent-100 text-accent-900'
                                    : 'bg-background-200/70 text-foreground-600'
                              }`}
                            >
                              {finding.severity}
                            </span>
                            <span>
                              <strong className="font-semibold">{finding.label}</strong> — {finding.detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-secondary-900 bg-secondary-100/60 rounded-md px-3 py-2">
                        <i className="ri-shield-check-line mr-1"></i>
                        Aucun motif dangereux détecté dans le contenu de l'application.
                      </p>
                    )}

                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-foreground-600 font-medium">Script SQL de provisionnement</summary>
                      <pre className="mt-2 text-[10px] bg-background-50 border border-background-200/60 rounded-md p-3 max-h-48 overflow-auto whitespace-pre-wrap text-foreground-600">
                        {submission.provisionSql}
                      </pre>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <button
                          onClick={() => copySql(submission)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/60 text-foreground-600 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-100"
                        >
                          <i className={copiedId === submission.appId ? 'ri-check-line text-accent-600' : 'ri-file-copy-line'}></i>
                          {copiedId === submission.appId ? 'Copié' : 'Copier le script'}
                        </button>
                        <Link
                          to="/superadmin/database"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-background-200/60 text-foreground-600 text-[10px] font-medium whitespace-nowrap no-underline hover:bg-background-100"
                        >
                          <i className="ri-database-2-line"></i>
                          Ouvrir la base de données
                        </Link>
                      </div>
                    </details>

                    {submission.status === 'pending' && (
                      <div className="space-y-2">
                        <textarea
                          value={noteDrafts[submission.appId] || ''}
                          onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [submission.appId]: e.target.value }))}
                          rows={2}
                          placeholder="Note de validation (facultative) — visible par l'équipe"
                          className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-foreground-300 resize-y"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => decide(submission, 'approved')}
                            disabled={busyId === submission.appId}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50"
                          >
                            <i className={busyId === submission.appId ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
                            Approuver et publier
                          </button>
                          <button
                            onClick={() => decide(submission, 'rejected')}
                            disabled={busyId === submission.appId}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 border border-background-200/70 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                          >
                            <i className="ri-close-line"></i>
                            Refuser
                          </button>
                        </div>
                      </div>
                    )}

                    {submission.status !== 'pending' && (
                      <p className="text-[11px] text-foreground-500">
                        Décision enregistrée le{' '}
                        {submission.reviewedAt ? new Date(submission.reviewedAt).toLocaleString('fr-FR') : '—'}
                        {submission.reviewerNote ? ` · ${submission.reviewerNote}` : ''}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}